/* Real-time notification lifecycle:
 * 1. subscribe onMyNotification
 * 2. fetch the baseline
 * 3. re-list on reconnect / tab refocus
 */
import { generateClient } from 'aws-amplify/api';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/store/store';
import * as notificationOps from './notificationOperations';
import { fromPushedNotification } from './notificationModels';
import { fetchMyNotifications, notificationReceived } from './notificationsSlice';

/** Minimum gap between visibility-triggered baseline re-fetches, to avoid flooding on rapid tab switches. */
const REFETCH_INTERVAL_MS = 30_000;

export const useNotificationsSubscription = (isAuthenticated: boolean): void => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const client = generateClient();
    let closed = false;
    let lastFetchTime = 0;

    // 1. subscribe onMyNotification
    const subscription = client.graphql({ query: notificationOps.onMyNotification }).subscribe({
      next: ({ data }) => {
        const item = data?.onMyNotification;
        if (item) {
          dispatch(notificationReceived(fromPushedNotification(item)));
        }
      },
      error: (err) => {
        console.warn('onMyNotification subscription error', err);
      }
    });

    // 2. fetch the baseline
    dispatch(fetchMyNotifications());

    // 3. re-list when the tab becomes visible again. Uses `visibilitychange` and throttles to
    // at most once per interval so rapid tab switching can't flood the backend.
    const onVisibilityChange = () => {
      const now = Date.now();
      if (!closed && document.visibilityState === 'visible' && now - lastFetchTime > REFETCH_INTERVAL_MS) {
        lastFetchTime = now;
        dispatch(fetchMyNotifications());
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      subscription.unsubscribe();
    };
  }, [dispatch, isAuthenticated]);
};
